import {
    AlertExpression,
    AlertExpression_Type,
    Condition,
    Condition_CompositeCondition_Operator,
    Condition_SecurityAlertCondition,
    Condition_SecurityAlertCondition_Operator,
    UserAlert,
} from '../proto/generated/nexus'
import { Order, OrderExecutionParams, OrderStatus, OrderType, Product, TransactionType } from '../proto/generated/order'
import { Security } from '../proto/generated/security'
import { TimeBucket_TimeSeries } from '../proto/generated/common'
import { ChartType } from '../proto/generated/chart'
import { Indicator } from '../proto/generated/indicator'
import { Time } from 'lightweight-charts'
import { ChartAlertSnapshot } from '../components/alerts/alertModal.typings'
import type { ConditionalModalChartVariants } from '../reducer/alerts.reducer'
import { FixedExpressionOutputTypes } from '../components/alerts/alertExpressionDropdown/alertExpressionDropdown.typings'
import { createUserAlertFromSnapshot } from './alerts'
import { convertKeyValueArrayToMap, getStrategyChartParamsFromTradeOrders } from './common'
import { TradeOrder } from '../reducer/strategyBuilder.reducer'
import { Position } from '../proto/generated/position'
import { getIndicatorDisplayNameForAlert } from './indicators'
import { getOperatorSource, getPrimarySecurityAlertCondition, getSelectedValue } from './alerts'
import { operatorDisplayNames } from '../components/alerts/alertOperatorDropdown/alertOperatorDropdown'
import { v4 as uuid } from 'uuid'
import { isPlaceholderStrategyGroupName, ORDER_TYPES_STRATEGY_AUXILIARY } from './orders'
import { effectiveOrderFilledQuantity, ICEBERG_NON_TERMINAL_STATUSES } from './icebergOrders'
import { getSecurityKey } from './security'

/** Non-terminal CE/CX statuses eligible for modify / live sibling lookup. */
export const LIVE_CONDITIONAL_AUXILIARY_STATUSES: OrderStatus[] = [
    OrderStatus.INACTIVE,
    OrderStatus.OPEN,
    OrderStatus.PENDING,
    OrderStatus.TRIGGER_PENDING,
]

export function isLiveConditionalAuxiliaryOrder(order: Order | undefined): boolean {
    if (!order?.orderId) return false
    return LIVE_CONDITIONAL_AUXILIARY_STATUSES.includes(order.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
}

export type ConditionalEntryState = {
    enabled: boolean
    condition?: Condition
}

/** Basket CE/CX row title: meaningful `strategyGroup` from broker/store, else `{underlying} Basket`. */
export function basketConditionalRowLabelFromStrategyGroup(
    strategyGroup: string | undefined,
    underlyingSymbolOrCode: string | undefined
): string {
    const sg = strategyGroup?.trim()
    if (sg && !isPlaceholderStrategyGroupName(sg)) return sg
    const u = underlyingSymbolOrCode?.trim()
    return u ? `${u} Basket` : 'Basket'
}

export function isConditionalLegOrderType(orderType: OrderType | undefined): boolean {
    return orderType === OrderType.CONDITIONAL_ENTRY || orderType === OrderType.CONDITIONAL_EXIT
}

export function isStrategyAuxiliaryOrderType(orderType: OrderType | undefined): boolean {
    return orderType != null && ORDER_TYPES_STRATEGY_AUXILIARY.includes(orderType)
}

/** `referencePrice` is the fixed RHS in display/human units (e.g. rupees), not exchange-native × multiplier. */
export function createDefaultEntryCondition(
    security: Security,
    referencePrice: number,
    operator: Condition_SecurityAlertCondition_Operator = Condition_SecurityAlertCondition_Operator.CROSSING
): Condition {
    const lhsExpression: AlertExpression = {
        type: AlertExpression_Type.SIMPLE_SECURITY,
        timeBucket: {
            timeSeries: TimeBucket_TimeSeries.SECOND,
            value: 1,
        },
        simpleSecurity: {
            security: {
                exchange: security.exchange,
                code: security.code,
            },
            source: getOperatorSource(operator),
        },
    }
    const rhsExpression: AlertExpression = {
        type: AlertExpression_Type.FIXED_VALUE,
        fixedValue: referencePrice,
        timeBucket: undefined,
    }
    const securityAlertCondition: Condition_SecurityAlertCondition = {
        lhsExpression,
        rhsExpression,
        operator,
    }
    return { securityAlertCondition }
}

function isSecurityAlertLeafReady(sac: Condition_SecurityAlertCondition | undefined): boolean {
    if (!sac?.lhsExpression || !sac.rhsExpression) return false
    if (sac.operator === Condition_SecurityAlertCondition_Operator.OPERATOR_UNKNOWN) return false
    if (sac.rhsExpression.type === AlertExpression_Type.FIXED_VALUE) {
        const v = sac.rhsExpression.fixedValue
        return typeof v === 'number' && !Number.isNaN(v)
    }
    return true
}

/** True when `condition` has at least one usable security rule leaf (single or composite). */
export function hasConditionalRuleContent(condition: Condition | undefined): boolean {
    if (!condition) return false
    if (condition.securityAlertCondition) {
        return !!(
            condition.securityAlertCondition.lhsExpression && condition.securityAlertCondition.rhsExpression
        )
    }
    const children = condition.compositeCondition?.condition
    if (!children?.length) return false
    return children.some((ch) => hasConditionalRuleContent(ch))
}

/** Fixed numeric threshold (e.g. price > 2500) needs a pinned template symbol; field-vs-field rules can use chart symbol. */
export function conditionalRuleRequiresPinnedSymbol(condition: Condition | undefined): boolean {
    if (!condition) return false
    if (condition.securityAlertCondition) {
        const { lhsExpression, rhsExpression } = condition.securityAlertCondition
        return (
            lhsExpression?.type === AlertExpression_Type.FIXED_VALUE ||
            rhsExpression?.type === AlertExpression_Type.FIXED_VALUE
        )
    }
    const children = condition.compositeCondition?.condition
    if (!children?.length) return false
    return children.some((ch) => conditionalRuleRequiresPinnedSymbol(ch))
}

export function anyConditionalRuleRequiresPinnedSymbol(
    ...conditions: (Condition | undefined)[]
): boolean {
    return conditions.some(conditionalRuleRequiresPinnedSymbol)
}

/** Primary rule leaf LHS is a spread / security expression (basket strategy chart). */
export function conditionalPrimaryUsesSpreadLhs(condition: Condition | undefined): boolean {
    const sac = getPrimarySecurityAlertCondition(condition)
    return sac?.lhsExpression?.type === AlertExpression_Type.SPREAD_SECURITY
}

function isConditionTreeReadyForConditionalEntry(cond: Condition | undefined): boolean {
    if (!cond) return false
    if (cond.securityAlertCondition) {
        return isSecurityAlertLeafReady(cond.securityAlertCondition)
    }
    const children = cond.compositeCondition?.condition
    if (!children?.length) return false
    const op = cond.compositeCondition?.logicalOperator
    if (op === Condition_CompositeCondition_Operator.OPERATOR_UNKNOWN) return false
    return children.every((ch) => isConditionTreeReadyForConditionalEntry(ch))
}

export function isConditionalEntryReady(state: ConditionalEntryState): boolean {
    if (!state.enabled) return true
    return isConditionTreeReadyForConditionalEntry(state.condition)
}

export function mergeOrderExecutionParamsWithCondition(
    base: OrderExecutionParams | undefined,
    entryCondition: Condition | undefined
): OrderExecutionParams | undefined {
    if (!entryCondition) {
        return base
    }
    return {
        ...(base ?? {}),
        condition: entryCondition,
    }
}

export function orderExecutionParamsForConditionalEntry(
    base: OrderExecutionParams | undefined,
    state: ConditionalEntryState,
    opts?: { stripWhenDisabled?: boolean }
): OrderExecutionParams | undefined {
    const stripWhenDisabled = opts?.stripWhenDisabled !== false
    if (state.enabled && state.condition) {
        return mergeOrderExecutionParamsWithCondition(base, state.condition)
    }
    if (!stripWhenDisabled) {
        return base
    }
    if (base?.condition !== undefined) {
        return { ...base, condition: undefined }
    }
    return base
}

export type SummarizeConditionalEntryOpts = {
    indicators?: Indicator[]
}

function resolveStrategySecuritiesForKeys(
    keys: string[],
    orders: TradeOrder[],
    positions?: Position[]
): Security[] {
    const out: Security[] = []
    for (const key of keys) {
        let sec: Security | undefined
        for (const o of orders) {
            if (o.security && getSecurityKey(o.security.exchange, o.security.code) === key) {
                sec = o.security
                break
            }
        }
        if (!sec && positions) {
            for (const p of positions) {
                if (p.security && getSecurityKey(p.security.exchange, p.security.code) === key) {
                    sec = p.security
                    break
                }
            }
        }
        if (!sec) return []
        out.push(sec)
    }
    return out
}

/**
 * Strategy-chart expression snapshot for conditional modal (no price/volume prefill — data zeroed).
 */
export function buildStrategySpreadConditionalSnapshot(
    orders: TradeOrder[],
    positions: Position[] | undefined
): ChartAlertSnapshot | null {
    const { param, securities: keys } = getStrategyChartParamsFromTradeOrders(orders, positions)
    if (!param || !keys.length) return null
    const securityList = resolveStrategySecuritiesForKeys(keys, orders, positions)
    if (securityList.length !== keys.length) return null
    return {
        securityList,
        comparableSecurities: [],
        availableTimeBuckets: [],
        expressionMode: true,
        expression: param,
        indicators: [],
        chartType: ChartType.CANDLESTICK,
        data: {
            open: 0,
            high: 0,
            low: 0,
            close: 0,
            time: 0 as Time,
            customValues: { volume: 0 },
        },
        timeBucket: {
            timeSeries: TimeBucket_TimeSeries.SECOND,
            value: 1,
        },
    }
}

export function buildQuickTradeChartSnapshot(
    security: Security,
    lastTradedPriceNative: number,
    volume: number
): ChartAlertSnapshot {
    return {
        securityList: [security],
        comparableSecurities: [],
        availableTimeBuckets: [],
        expressionMode: false,
        expression: '',
        indicators: [],
        chartType: ChartType.CANDLESTICK,
        data: {
            open: lastTradedPriceNative,
            high: lastTradedPriceNative,
            low: lastTradedPriceNative,
            close: lastTradedPriceNative,
            time: 0 as Time,
            customValues: {
                volume,
            },
        },
        timeBucket: {
            timeSeries: TimeBucket_TimeSeries.SECOND,
            value: 1,
        },
    }
}
export function chartSnapshotWithZeroedConditionalPrefill(snapshot: ChartAlertSnapshot): ChartAlertSnapshot {
    const prevCustom = snapshot.data?.customValues ?? {}
    return {
        ...snapshot,
        data: {
            ...snapshot.data,
            open: 0,
            high: 0,
            low: 0,
            close: 0,
            customValues: {
                ...prevCustom,
                volume: 0,
            },
        },
    }
}

/**
 * When the active chart matches (underlying symbol or same strategy expression), reuse its snapshot for the conditional alert UI.
 * Strategy (expression) snapshots must not be replaced by an underlying-only chart — that would drop expression mode and show the wrong symbol label.
 */
export function mergeQuickTradeSnapshotWithMatchingChart(
    quickTradeSnapshot: ChartAlertSnapshot,
    chartSnapshot: ChartAlertSnapshot | null | undefined,
    lhsSecurity: Security
): ChartAlertSnapshot {
    if (!chartSnapshot?.securityList?.length) {
        return chartSnapshotWithZeroedConditionalPrefill(quickTradeSnapshot)
    }
    if (quickTradeSnapshot.expressionMode) {
        if (
            chartSnapshot.expressionMode &&
            quickTradeSnapshot.expression &&
            chartSnapshot.expression === quickTradeSnapshot.expression
        ) {
            return chartSnapshot
        }
        return chartSnapshotWithZeroedConditionalPrefill(quickTradeSnapshot)
    }
    const matches = chartSnapshot.securityList.some(
        (s) => s.exchange === lhsSecurity.exchange && s.code === lhsSecurity.code
    )
    return matches ? chartSnapshot : chartSnapshotWithZeroedConditionalPrefill(quickTradeSnapshot)
}

/** Merge chart snapshot default alert with an optional existing condition (native units; supports composite). */
export function userAlertForConditionalEntryModal(
    snapshot: ChartAlertSnapshot,
    seedCondition: Condition | undefined
): UserAlert {
    const base = createUserAlertFromSnapshot(snapshot)
    if (!seedCondition || !hasConditionalRuleContent(seedCondition)) {
        return base
    }
    const sac = getPrimarySecurityAlertCondition(seedCondition)
    const lhs = sac?.lhsExpression
    let headerSecurity = base.security
    if (lhs?.type === AlertExpression_Type.SIMPLE_SECURITY) {
        const s = lhs.simpleSecurity?.security
        if (s?.code != null && s.code !== '' && s.exchange != null) {
            headerSecurity = { exchange: s.exchange, code: s.code }
        }
    }
    return {
        ...base,
        condition: structuredClone(seedCondition),
        security: headerSecurity,
    }
}

/**
 * When the user edited the rule on the **underlying** chart tab, persist the primary LHS
 * `simpleSecurity.security` as the underlying instrument (from `variants.underlying`), not the option leg.
 */
export function rewriteConditionalLhsSecurityForUnderlyingChartRef(
    condition: Condition,
    chartRef: 'underlying' | 'spread' | undefined | null,
    variants: ConditionalModalChartVariants | undefined
): Condition {
    if (chartRef !== 'underlying') {
        return structuredClone(condition)
    }
    const und = variants?.underlying?.securityList?.[0]
    if (!und?.code) {
        return structuredClone(condition)
    }
    const next = structuredClone(condition)
    const sac = getPrimarySecurityAlertCondition(next)
    if (!sac?.lhsExpression || sac.lhsExpression.type !== AlertExpression_Type.SIMPLE_SECURITY) {
        return next
    }
    const prev = sac.lhsExpression.simpleSecurity
    const source = prev?.source && prev.source.length > 0 ? prev.source : 'close'
    sac.lhsExpression.simpleSecurity = {
        security: { exchange: und.exchange, code: und.code },
        source,
    }
    return next
}

function summarizeOneSecurityAlertCondition(
    c: Condition_SecurityAlertCondition,
    displaySymbol: string,
    ind: Indicator[]
): string {
    if (!c?.lhsExpression || !c?.rhsExpression || !c.operator) return ''
    const op = operatorDisplayNames[c.operator] ?? ''
    const lhsVal = getSelectedValue(c.lhsExpression, ind)

    let lhs = displaySymbol
    switch (c.lhsExpression.type) {
        case AlertExpression_Type.SIMPLE_SECURITY:
        case AlertExpression_Type.SPREAD_SECURITY: {
            const sec = c.lhsExpression.simpleSecurity || c.lhsExpression.spreadSecurity
            if (sec?.source === FixedExpressionOutputTypes.VOLUME || lhsVal[0] === FixedExpressionOutputTypes.VOLUME) {
                lhs = `${displaySymbol} vol`
            } else {
                lhs = displaySymbol
            }
            break
        }
        case AlertExpression_Type.TECHNICAL_INDICATOR: {
            const def = ind.find((i) => i.code === c.lhsExpression!.indicator!.indicatorCode)
            lhs = def
                ? getIndicatorDisplayNameForAlert(def, convertKeyValueArrayToMap(c.lhsExpression.indicator!.params), ind)
                : lhsVal[0] || displaySymbol
            break
        }
        default:
            break
    }

    let rhs = '…'
    if (c.rhsExpression.type === AlertExpression_Type.FIXED_VALUE && c.rhsExpression.fixedValue != null) {
        const v = c.rhsExpression.fixedValue
        rhs = String(v)
    } else if (
        c.rhsExpression.type === AlertExpression_Type.SIMPLE_SECURITY ||
        c.rhsExpression.type === AlertExpression_Type.SPREAD_SECURITY
    ) {
        const rhsVal = getSelectedValue(c.rhsExpression, ind)
        const sec = c.rhsExpression.simpleSecurity || c.rhsExpression.spreadSecurity
        if (rhsVal[0] === FixedExpressionOutputTypes.VOLUME || sec?.source === FixedExpressionOutputTypes.VOLUME) {
            rhs = `${displaySymbol} vol`
        } else {
            rhs = `${displaySymbol} px`
        }
    } else if (c.rhsExpression.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
        const def = ind.find((i) => i.code === c.rhsExpression!.indicator!.indicatorCode)
        rhs = def
            ? getIndicatorDisplayNameForAlert(def, convertKeyValueArrayToMap(c.rhsExpression.indicator!.params), ind)
            : '…'
    }

    return `${lhs} ${op} ${rhs}`.trim()
}

export function summarizeConditionalEntry(
    condition: Condition | undefined,
    displaySymbol: string,
    opts?: SummarizeConditionalEntryOpts
): string {
    if (!condition) return ''
    const ind = opts?.indicators ?? []
    if (condition.securityAlertCondition) {
        return summarizeOneSecurityAlertCondition(condition.securityAlertCondition, displaySymbol, ind)
    }
    const comp = condition.compositeCondition
    const children = comp?.condition
    if (!children?.length) return ''
    const parts = children
        .map((ch) => summarizeConditionalEntry(ch, displaySymbol, opts))
        .filter((s) => s.length > 0)
    if (!parts.length) return ''
    const joiner =
        comp!.logicalOperator === Condition_CompositeCondition_Operator.OR ? ' OR ' : ' AND '
    return parts.length === 1 ? parts[0] : `(${parts.join(joiner)})`
}

// --- Batch-scoped legs (CE/CX live next to primary strategy legs) ---

export function findCancellableConditionalEntryLegInBatch(
    orders: Order[],
    basketId: string,
    batchId: string
): Order | undefined {
    return orders.find(
        (o) =>
            o.basketId === basketId &&
            o.batchId === batchId &&
            o.orderType === OrderType.CONDITIONAL_ENTRY &&
            o.orderId &&
            o.status !== OrderStatus.EXECUTED &&
            o.status !== OrderStatus.CANCELLED &&
            o.status !== OrderStatus.FAILED &&
            o.status !== OrderStatus.REJECTED
    )
}

/** Primary strategy legs in batch (excludes SL/TP/CE/CX). */
export function getNonAuxiliaryLegsInBatch(
    orders: Order[],
    basketId: string,
    batchId: string | undefined
): Order[] {
    if (!batchId || !basketId) return []
    return orders.filter(
        (o) =>
            o.basketId === basketId &&
            o.batchId === batchId &&
            !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType)
    )
}

const invertTransactionType = (t: TransactionType): TransactionType =>
    t === TransactionType.BUY ? TransactionType.SELL : TransactionType.BUY

/**
 * Transaction type for SL / target legs: opposite of the batch **entry** (primary) leg.
 * When Quick Trade edits an SL or target row, dialog `transactionType` is the exit side — do not use that to invert.
 * Falls back to `invert(fallbackTransactionType)` when no primary leg is found (matches legacy bracket behavior for new trades).
 */
export function resolveBracketExitTransactionType(params: {
    orderHistory: Order[] | undefined
    anchorBasketId: string | undefined
    batchId: string | undefined
    security?: { code?: string; exchange?: number | undefined }
    fallbackTransactionType: TransactionType
}): TransactionType {
    const { orderHistory, anchorBasketId, batchId, security, fallbackTransactionType } = params
    if (!orderHistory?.length || !batchId) {
        return invertTransactionType(fallbackTransactionType)
    }

    let mains: Order[] = []
    if (anchorBasketId) {
        mains = getNonAuxiliaryLegsInBatch(orderHistory, anchorBasketId, batchId)
    }
    if (!mains.length) {
        mains = orderHistory.filter((o) => {
            if (o.batchId !== batchId) return false
            if (ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType)) return false
            if (!security?.code || security.exchange == null) return true
            return o.security?.code === security.code && o.security?.exchange === security.exchange
        })
    }

    const primary =
        mains.find((o) =>
            [
                OrderStatus.OPEN,
                OrderStatus.PENDING,
                OrderStatus.TRIGGER_PENDING,
                OrderStatus.PARTIALLY_FILLED,
            ].includes(o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
        ) ??
        mains.find((o) => o.status === OrderStatus.EXECUTED) ??
        mains[0]

    const entry = primary?.transactionType
    if (entry === TransactionType.BUY || entry === TransactionType.SELL) {
        return invertTransactionType(entry)
    }
    return invertTransactionType(fallbackTransactionType)
}

/** Open size on a book row: grouped UI uses `quantity`; API/proto positions use `closePosition` / overnight. */
function bookLegOpenQuantity(p: {
    basketId?: string
    quantity?: number
    closePosition?: number
    overnightPosition?: number
}): number {
    if (typeof p.quantity === 'number' && p.quantity !== 0) return p.quantity
    if (typeof p.closePosition === 'number' && p.closePosition !== 0) return p.closePosition
    if (typeof p.overnightPosition === 'number' && p.overnightPosition !== 0) return p.overnightPosition
    return 0
}

/**
 * True when the positions store has any non-zero leg for this basket.
 * Redux holds proto {@link Position} rows (`closePosition`), not grouped `quantity` — older baskets rely on this.
 */
export function basketBookHasOpenExposureForBasket(
    positions:
        | readonly {
              basketId?: string
              quantity?: number
              closePosition?: number
              overnightPosition?: number
          }[]
        | undefined,
    basketId: string | undefined
): boolean {
    const id = basketId?.trim()
    if (!id || !positions?.length) return false
    return positions.some((p) => (p.basketId ?? '').trim() === id && bookLegOpenQuantity(p) !== 0)
}

function orderHasPartialFill(o: Order): boolean {
    if (o.status === OrderStatus.PARTIALLY_FILLED) return true
    const f = effectiveOrderFilledQuantity(o)
    const q = o.quantity ?? 0
    return f > 0 && q > 0 && f < q
}

/**
 * Batch id for Analyze/Adjust from positions so CE/CX rows match Redux `batchId`.
 */
export function resolveBatchIdForAnalyzeFromOrders(
    orders: Order[] | undefined,
    basketId: string | undefined
): string | undefined {
    if (!orders?.length || !basketId) return undefined
    const pool = orders.filter((o) => o.basketId === basketId && o.batchId)
    if (!pool.length) return undefined
    const cx = pool.find(
        (o) =>
            o.orderType === OrderType.CONDITIONAL_EXIT &&
            o.status !== OrderStatus.EXECUTED &&
            o.status !== OrderStatus.CANCELLED &&
            o.status !== OrderStatus.FAILED &&
            o.status !== OrderStatus.REJECTED
    )
    if (cx?.batchId) return cx.batchId
    const filledMain = pool.find(
        (o) =>
            !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType) &&
            (o.status === OrderStatus.EXECUTED ||
                o.status === OrderStatus.PARTIALLY_FILLED ||
                effectiveOrderFilledQuantity(o) > 0)
    )
    if (filledMain?.batchId) return filledMain.batchId
    const workingMain = pool.find(
        (o) =>
            !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType) &&
            ICEBERG_NON_TERMINAL_STATUSES.includes(o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
    )
    return workingMain?.batchId
}

/**
 * Prefer {@link resolveBatchIdForAnalyzeFromOrders}; else latest order in basket (so batch id always matches history).
 */
export function resolveBatchIdForBasketFromOrders(
    orders: Order[] | undefined,
    basketId: string | undefined
): string | undefined {
    const preferred = resolveBatchIdForAnalyzeFromOrders(orders, basketId)
    if (preferred) return preferred
    if (!orders?.length || !basketId) return undefined
    const pool = orders.filter((o) => o.basketId === basketId && o.batchId)
    if (!pool.length) return undefined
    const sorted = [...pool].sort((a, b) => (b.orderDate ?? 0) - (a.orderDate ?? 0))
    return sorted[0].batchId
}

/** Basket-scoped conditional exit leg (underlying reference row, `isBasket: true`). */
export function findBasketConditionalExitLeg(
    orders: Order[] | undefined,
    basketId: string,
    batchId: string | undefined
): Order | undefined {
    const pool = (orders ?? []).filter((o) => {
        if (o.basketId !== basketId) return false
        if (o.orderType !== OrderType.CONDITIONAL_EXIT) return false
        if (!o.isBasket) return false
        if (batchId && o.batchId !== batchId) return false
        return true
    })
    return (
        pool.find((o) => o.status === OrderStatus.INACTIVE) ??
        pool.find((o) =>
            [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(
                o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN
            )
        )
    )
}

export function batchHasOpenBasketTaggedPrimaryLeg(
    orders: Order[],
    basketId: string,
    batchId: string
): boolean {
    return orders.some(
        (o) =>
            o.basketId === basketId &&
            o.batchId === batchId &&
            o.isBasket &&
            !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType) &&
            ICEBERG_NON_TERMINAL_STATUSES.includes(o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
    )
}

export function batchHasAnyOpenPrimaryLeg(orders: Order[], basketId: string, batchId: string): boolean {
    if (!basketId || !batchId) return false
    return orders.some(
        (o) =>
            o.basketId === basketId &&
            o.batchId === batchId &&
            !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType) &&
            ICEBERG_NON_TERMINAL_STATUSES.includes(o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
    )
}

export function batchHasAnyPrimaryLegFill(
    orders: Order[],
    basketId: string,
    batchId: string | undefined
): boolean {
    const mains = getNonAuxiliaryLegsInBatch(orders, basketId, batchId)
    return mains.some(
        (m) =>
            orderHasPartialFill(m) ||
            effectiveOrderFilledQuantity(m) > 0 ||
            m.status === OrderStatus.EXECUTED
    )
}

/**
 * New CE leg (BE-B-CE-13): allow only when this basket/batch has no primary legs in history yet
 * and no live CE gate. Once mains exist (live or filled), placing a new CE is denied.
 * First compose (mains + CE in one request) still works because history is empty at check time.
 */
export function batchAllowsNewConditionalEntryLeg(
    orders: Order[],
    basketId: string,
    batchId: string | undefined
): boolean {
    if (!batchId || !basketId) return true
    if (
        findLiveConditionalAuxiliaryLeg(orders, {
            orderType: OrderType.CONDITIONAL_ENTRY,
            basketId,
            batchId,
            isBasket: true,
        })
    ) {
        return false
    }
    const mains = getNonAuxiliaryLegsInBatch(orders, basketId, batchId)
    return mains.length === 0
}

/** True when basket already has orders or open book exposure — required for standalone CX (BE-B-CX-07). */
export function basketAllowsStandaloneCxPlace(
    orders: Order[] | undefined,
    positions:
        | readonly {
              basketId?: string
              quantity?: number
              closePosition?: number
              overnightPosition?: number
          }[]
        | undefined,
    basketId: string | undefined
): boolean {
    const id = basketId?.trim()
    if (!id) return false
    if (basketBookHasOpenExposureForBasket(positions, id)) return true
    return (orders ?? []).some((o) => (o.basketId ?? '').trim() === id && !!o.orderId)
}

/** CE/CX wire product follows primary legs / UI selection — never force CNC. */
export function resolveConditionalAuxProduct(legProduct: Product | undefined): Product {
    if (legProduct === Product.INTRADAY) return Product.INTRADAY
    if (legProduct === Product.CNC) return Product.CNC
    if (legProduct === Product.MARGIN) return Product.MARGIN
    return Product.MARGIN
}

/**
 * New CX leg: empty batch, all primaries done, any fill progress, all live with zero fill, or mixed progress
 * (see call sites in order bar).
 */
export function batchAllowsConditionalExitPlacement(
    orders: Order[],
    basketId: string,
    batchId: string | undefined
): boolean {
    if (!batchId || !basketId) return true
    const mains = getNonAuxiliaryLegsInBatch(orders, basketId, batchId)
    if (mains.length === 0) return true
    const allDone = mains.every(
        (m) =>
            m.status === OrderStatus.EXECUTED ||
            effectiveOrderFilledQuantity(m) >= (m.quantity ?? 0)
    )
    if (allDone) return true
    const allLiveZeroExecution = mains.every(
        (m) =>
            ICEBERG_NON_TERMINAL_STATUSES.includes(m.status ?? OrderStatus.ORDER_STATUS_UNKNOWN) &&
            m.status !== OrderStatus.PARTIALLY_FILLED &&
            effectiveOrderFilledQuantity(m) <= 0
    )
    if (allLiveZeroExecution) return true
    return mains.some(
        (m) =>
            orderHasPartialFill(m) ||
            effectiveOrderFilledQuantity(m) > 0 ||
            m.status === OrderStatus.EXECUTED
    )
}

export function batchAllPrimaryLegsFullyFilled(
    orders: Order[],
    basketId: string,
    batchId: string | undefined
): boolean {
    if (!batchId || !basketId || !orders?.length) return false
    const mains = getNonAuxiliaryLegsInBatch(orders, basketId, batchId)
    if (mains.length === 0) return false
    return mains.every(
        (m) =>
            m.status === OrderStatus.EXECUTED ||
            effectiveOrderFilledQuantity(m) >= (m.quantity ?? 0)
    )
}

/**
 * True when modifying SL/TP or a primary leg that is still non-terminal — Quick Trade hides CE/CX add tiles on that row.
 * False when modifying CE/CX rows (edit on-row).
 */
export function quickTradeModifySuppressesConditionalTilesOnRow(modifyingOrder: Order | undefined): boolean {
    if (!modifyingOrder) return false
    if (isConditionalLegOrderType(modifyingOrder.orderType)) return false
    if (isStrategyAuxiliaryOrderType(modifyingOrder.orderType)) return true
    return ICEBERG_NON_TERMINAL_STATUSES.includes(
        modifyingOrder.status ?? OrderStatus.ORDER_STATUS_UNKNOWN
    )
}

/** CX order: UI can edit rule unless terminal (incl. EXECUTED — BE-B-CX-06). */
export function conditionalExitLegAllowsRuleEditInUi(order: Order | undefined): boolean {
    if (!order?.orderId) return true
    const s = order.status ?? OrderStatus.ORDER_STATUS_UNKNOWN
    return (
        s !== OrderStatus.CANCELLED &&
        s !== OrderStatus.FAILED &&
        s !== OrderStatus.REJECTED &&
        s !== OrderStatus.EXECUTED
    )
}

/**
 * CE order: rule can be edited only while the order is not yet live (e.g. inactive / trigger-waiting).
 * Once condition is met and the leg is OPEN or partially working, the rule must not be changed from UI.
 */
export function conditionalEntryLegAllowsRuleEditInUi(order: Order | undefined): boolean {
    if (!order?.orderId) return true
    if (order.orderType !== OrderType.CONDITIONAL_ENTRY) return true
    const s = order.status ?? OrderStatus.ORDER_STATUS_UNKNOWN
    if (s === OrderStatus.OPEN || s === OrderStatus.PARTIALLY_FILLED) return false
    return (
        s !== OrderStatus.CANCELLED &&
        s !== OrderStatus.FAILED &&
        s !== OrderStatus.REJECTED &&
        s !== OrderStatus.EXECUTED
    )
}

export function findCancellableConditionalAuxiliaryLegsInBatch(
    orders: Order[],
    basketId: string,
    batchId: string
): Order[] {
    const entry = findCancellableConditionalEntryLegInBatch(orders, basketId, batchId)
    const exits = orders.filter(
        (o) =>
            o.basketId === basketId &&
            o.batchId === batchId &&
            o.orderType === OrderType.CONDITIONAL_EXIT &&
            o.orderId &&
            o.status !== OrderStatus.EXECUTED &&
            o.status !== OrderStatus.CANCELLED &&
            o.status !== OrderStatus.FAILED &&
            o.status !== OrderStatus.REJECTED
    )
    return entry ? [entry, ...exits] : exits
}

export function batchHasNonTerminalConditionalExit(
    orders: Order[] | undefined,
    basketId: string,
    batchId?: string
): boolean {
    if (!basketId || !orders?.length) return false
    return orders.some((o) => {
        if (o.basketId !== basketId || o.orderType !== OrderType.CONDITIONAL_EXIT) return false
        if (batchId && o.batchId && o.batchId !== batchId) return false
        return ICEBERG_NON_TERMINAL_STATUSES.includes(o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
    })
}

/** Primary leg still fully unfilled (no partial) — CE rule may be edited in UI. */
export function primaryLegUnfilledForEntryRuleEdit(order: Order | undefined): boolean {
    if (!order) return true
    if (order.status === OrderStatus.PARTIALLY_FILLED) return false
    return effectiveOrderFilledQuantity(order) <= 0
}

/**
 * Basket CE rule edit: any primary in `OPEN` blocks edits (legs are live on the book).
 * Basket fired CE may be `CANCELLED` on the backend rather than `OPEN`, so mains lifecycle
 * must gate edits — do not rely on CE row `OPEN` alone.
 */
export function primaryLegAllowsBasketConditionalEntryRuleEdit(order: Order | undefined): boolean {
    if (!order) return true
    const s = order.status ?? OrderStatus.ORDER_STATUS_UNKNOWN
    if (s === OrderStatus.OPEN) return false
    return primaryLegUnfilledForEntryRuleEdit(order)
}

/** All `isBasket` primaries in batch allow basket CE rule edit (no `OPEN` main, no partial fill). */
export function basketPrimaryLegsAllAllowEntryRuleEdit(
    orders: Order[],
    basketId: string,
    batchId: string
): boolean {
    const mains = orders.filter(
        (o) =>
            o.basketId === basketId &&
            o.batchId === batchId &&
            o.isBasket &&
            !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType)
    )
    if (mains.length === 0) return true
    return mains.every(primaryLegAllowsBasketConditionalEntryRuleEdit)
}

function findFirstNonAuxiliarySameBatch(
    history: Order[],
    ref: Pick<Order, 'batchId' | 'basketId'>
): Order | undefined {
    return history.find(
        (o) =>
            o.batchId === ref.batchId &&
            (ref.basketId ? o.basketId === ref.basketId : true) &&
            !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType)
    )
}

/** Quick Trade: conditional ENTRY rule (CE / bundled entry on main). */
export function quickTradeAllowsConditionalEntryRuleEditing(
    orderHistory: Order[] | undefined,
    modifyingOrder: Order | undefined
): boolean {
    if (!modifyingOrder) return true
    const hist = orderHistory ?? []

    if (modifyingOrder.orderType === OrderType.CONDITIONAL_EXIT) {
        return true
    }

    if (modifyingOrder.orderType === OrderType.CONDITIONAL_ENTRY) {
        if (!conditionalEntryLegAllowsRuleEditInUi(modifyingOrder)) return false
        if (modifyingOrder.isBasket && modifyingOrder.batchId && modifyingOrder.basketId) {
            return basketPrimaryLegsAllAllowEntryRuleEdit(
                hist,
                modifyingOrder.basketId,
                modifyingOrder.batchId
            )
        }
        const main = findFirstNonAuxiliarySameBatch(hist, modifyingOrder)
        if (main) return primaryLegUnfilledForEntryRuleEdit(main)
        return true
    }

    if (
        modifyingOrder.isBasket &&
        modifyingOrder.batchId &&
        modifyingOrder.basketId &&
        !isStrategyAuxiliaryOrderType(modifyingOrder.orderType)
    ) {
        const ceLegs = hist.filter(
            (o) =>
                o.basketId === modifyingOrder.basketId &&
                o.batchId === modifyingOrder.batchId &&
                o.orderType === OrderType.CONDITIONAL_ENTRY &&
                isLiveConditionalAuxiliaryOrder(o)
        )
        if (ceLegs.some((o) => !conditionalEntryLegAllowsRuleEditInUi(o))) return false
        return basketPrimaryLegsAllAllowEntryRuleEdit(
            hist,
            modifyingOrder.basketId,
            modifyingOrder.batchId
        )
    }

    if (modifyingOrder.orderExecutionParams?.icebergNumberOfLegs) {
        const legs = hist.filter(
            (o) =>
                o.batchId === modifyingOrder.batchId &&
                o.basketId === modifyingOrder.basketId &&
                o.security?.code === modifyingOrder.security?.code &&
                o.security?.exchange === modifyingOrder.security?.exchange &&
                o.orderExecutionParams?.icebergNumberOfLegs
        )
        if (legs.length > 0) {
            return legs.every(primaryLegUnfilledForEntryRuleEdit)
        }
    }

    return primaryLegUnfilledForEntryRuleEdit(modifyingOrder)
}

function findLiveConditionalExitInBatchContext(hist: Order[], modifyingOrder: Order): Order | undefined {
    return findLiveConditionalAuxiliaryLeg(hist, {
        orderType: OrderType.CONDITIONAL_EXIT,
        batchId: modifyingOrder.batchId,
        basketId: modifyingOrder.basketId,
        security: modifyingOrder.security,
    })
}

/** Quick Trade: conditional EXIT rule (CX). Not blocked when CE is already OPEN. */
export function quickTradeAllowsConditionalExitRuleEditing(
    orderHistory: Order[] | undefined,
    modifyingOrder: Order | undefined
): boolean {
    if (!modifyingOrder) return true
    const hist = orderHistory ?? []

    if (modifyingOrder.orderType === OrderType.CONDITIONAL_EXIT) {
        return conditionalExitLegAllowsRuleEditInUi(modifyingOrder)
    }

    if (modifyingOrder.orderType === OrderType.CONDITIONAL_ENTRY) {
        const cx = findLiveConditionalAuxiliaryLeg(hist, {
            orderType: OrderType.CONDITIONAL_EXIT,
            batchId: modifyingOrder.batchId,
            basketId: modifyingOrder.basketId,
        })
        return cx ? conditionalExitLegAllowsRuleEditInUi(cx) : true
    }

    if (
        modifyingOrder.isBasket &&
        modifyingOrder.batchId &&
        modifyingOrder.basketId &&
        !isStrategyAuxiliaryOrderType(modifyingOrder.orderType)
    ) {
        const cx = findLiveConditionalAuxiliaryLeg(hist, {
            orderType: OrderType.CONDITIONAL_EXIT,
            basketId: modifyingOrder.basketId,
            batchId: modifyingOrder.batchId,
            isBasket: true,
        })
        return cx ? conditionalExitLegAllowsRuleEditInUi(cx) : true
    }

    if (modifyingOrder.orderExecutionParams?.icebergNumberOfLegs) {
        const cx = findLiveConditionalExitInBatchContext(hist, modifyingOrder)
        return cx ? conditionalExitLegAllowsRuleEditInUi(cx) : true
    }

    const cx = findLiveConditionalExitInBatchContext(hist, modifyingOrder)
    return cx ? conditionalExitLegAllowsRuleEditInUi(cx) : true
}

export function resolveAuxiliaryLegBatchId(params: {
    orders: Order[] | undefined
    basketId?: string | undefined
    existingAuxLeg?: Order
    submissionPrimaryBatchId?: string
    standaloneEntryBatchId?: string
}): string {
    const { orders, basketId, existingAuxLeg, submissionPrimaryBatchId, standaloneEntryBatchId } = params

    if (isLiveConditionalAuxiliaryOrder(existingAuxLeg) && existingAuxLeg!.batchId) {
        return existingAuxLeg!.batchId
    }
    if (submissionPrimaryBatchId) {
        return submissionPrimaryBatchId
    }
    if (basketId) {
        const fromAnalyze = resolveBatchIdForAnalyzeFromOrders(orders, basketId)
        if (fromAnalyze) return fromAnalyze
        const fromBasket = resolveBatchIdForBasketFromOrders(orders, basketId)
        if (fromBasket) return fromBasket
    }
    if (standaloneEntryBatchId) return standaloneEntryBatchId
    return uuid()
}

export function resolveAuxiliaryLegWireIds(params: {
    existingLeg?: Order
    fallbackClientOrderId: string
}): { orderId?: string; clientOrderId: string } {
    if (isLiveConditionalAuxiliaryOrder(params.existingLeg) && params.existingLeg?.orderId) {
        return {
            orderId: params.existingLeg.orderId,
            clientOrderId: params.existingLeg.clientOrderId ?? params.fallbackClientOrderId,
        }
    }
    return { clientOrderId: uuid() }
}

export function resolveAuxiliaryLegBasketWire(params: {
    anchorOrder?: Order
}): { isBasket: boolean; basketId?: string } {
    if (params.anchorOrder?.isBasket && params.anchorOrder.basketId) {
        return { isBasket: true, basketId: params.anchorOrder.basketId }
    }
    return { isBasket: false }
}

export function findLiveConditionalAuxiliaryLeg(
    orders: Order[] | undefined,
    params: {
        basketId?: string
        batchId?: string
        orderType: OrderType.CONDITIONAL_ENTRY | OrderType.CONDITIONAL_EXIT
        isBasket?: boolean
        security?: { code?: string; exchange?: number }
    }
): Order | undefined {
    const pool = (orders ?? []).filter((o) => {
        if (o.orderType !== params.orderType) return false
        if (!LIVE_CONDITIONAL_AUXILIARY_STATUSES.includes(o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)) {
            return false
        }
        if (params.basketId != null && o.basketId !== params.basketId) return false
        if (params.batchId != null && o.batchId !== params.batchId) return false
        if (params.isBasket != null && o.isBasket !== params.isBasket) return false
        if (params.security?.code != null && o.security?.code !== params.security.code) return false
        if (params.security?.exchange != null && o.security?.exchange !== params.security.exchange) {
            return false
        }
        return true
    })
    return (
        pool.find((o) => o.status === OrderStatus.INACTIVE) ??
        pool.find((o) =>
            [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(
                o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN
            )
        )
    )
}

